/**
 * AdminSandboxKycCard — surfaces Sandbox verification records for a target
 * user inside the admin KYC queue drawer, with an inline unlock/reject/approve
 * override control per record.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Lock, Unlock, Loader2, ShieldOff, Play } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Button, Card, Pill } from "../common/Primitives";
import ConfirmReasonDialog from "../common/ConfirmReasonDialog";

const LABELS = {
  pan: "Individual PAN",
  digilocker: "Aadhaar via DigiLocker",
  company_pan: "Company PAN",
  cin: "CIN / MCA",
  gstin: "GSTIN",
  din: "DIN",
};

function StatusPill({ rec }) {
  if (!rec) return <Pill tone="muted">Not run</Pill>;
  const s = (rec.status || "").toLowerCase();
  if (s === "succeeded") return <Pill tone="success">{rec.locked ? "Verified · Locked" : "Verified"}</Pill>;
  if (s === "pending") return <Pill tone="warn">Pending</Pill>;
  return <Pill tone="danger">{rec.status || "—"}</Pill>;
}

function summarise(kind, rec) {
  const p = rec?.parsed || {};
  if (kind === "pan") return rec ? `${rec.request_ref_masked || ""} · match=${p.name_match ? "yes" : "no"}` : "";
  if (kind === "digilocker") return [p.name, p.dob].filter(Boolean).join(" · ");
  if (kind === "company_pan") return rec ? `${rec.request_ref_masked || ""} · ${p.category || ""}` : "";
  if (kind === "cin") return [p.company_name, p.status].filter(Boolean).join(" · ");
  if (kind === "gstin") return [p.legal_name, p.status, p.state].filter(Boolean).join(" · ");
  if (kind === "din") return [p.name].filter(Boolean).join(" · ");
  return "";
}

export default function AdminSandboxKycCard({ userId, testId = "admin-sandbox-kyc" }) {
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [overrideBusy, setOverrideBusy] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [overrideFor, setOverrideFor] = useState(null); // { rec, action }
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/kyc/sandbox/${userId}`);
      setSummary(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const runOverride = (rec, action) => {
    // Approve = no note needed, run immediately. Reject/unlock = capture reason.
    if (action === "approve") {
      return executeOverride(rec, action, "");
    }
    setOverrideFor({ rec, action });
  };

  const executeOverride = async (rec, action, note) => {
    setOverrideBusy(`${rec.verification_id}:${action}`);
    try {
      await api.post(`/admin/kyc/sandbox/${userId}/override`, {
        verification_id: rec.verification_id,
        action,
        note,
      });
      toast.success(`${action.charAt(0).toUpperCase() + action.slice(1)}d.`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setOverrideBusy(null);
    }
  };

  const confirmOverride = async ({ reason }) => {
    const { rec, action } = overrideFor || {};
    if (!rec || !action) return;
    await executeOverride(rec, action, reason);
    setOverrideFor(null);
  };

  const runBulk = () => setBulkConfirmOpen(true);
  const confirmBulk = async () => {
    setBulkConfirmOpen(false);
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/admin/kyc/sandbox/${userId}/run-company`);
      const ran = Object.keys(data.ran || {});
      const skipped = Object.keys(data.skipped || {});
      const passes = ran.filter((k) => (data.ran[k] && (Array.isArray(data.ran[k]) ? data.ran[k].every((r) => r.ok) : data.ran[k].ok)));
      toast.success(`Ran ${ran.length} check(s) · ${passes.length} passed${skipped.length ? ` · ${skipped.length} skipped` : ""}`);
      if (skipped.length) {
        toast.info(`Skipped: ${skipped.map((k) => `${k} (${data.skipped[k]})`).join("; ")}`, { duration: 8000 });
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBulkBusy(false);
    }
  };

  const checks = summary?.checks || {};

  return (
    <Card testId={testId}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline">Sandbox verifications</div>
          <div className="text-[11px] font-mono text-ink-muted uppercase tracking-widest mt-0.5">
            Live · api.sandbox.co.in
          </div>
        </div>
        <div className="flex items-center gap-2">
          {summary?.identity_verified && (
            <Pill tone="success" data-testid={`${testId}-identity-badge`}>Founder ID ✓</Pill>
          )}
          {summary?.company_verified && (
            <Pill tone="success" data-testid={`${testId}-company-badge`}>Company ✓</Pill>
          )}
          <Button
            onClick={runBulk}
            disabled={bulkBusy || busy}
            className="text-xs !py-1 !px-2 inline-flex items-center gap-1"
            data-testid={`${testId}-run-all-btn`}
            title="Run CIN + GSTIN + Company PAN + DIN using data from the startup profile"
          >
            {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
            {bulkBusy ? "Running…" : "Run all company checks"}
          </Button>
          {busy && !bulkBusy && <Loader2 size={14} className="animate-spin text-ink-muted" />}
        </div>
      </div>

      <div className="divide-y divide-navy/10">
        {Object.keys(LABELS).map((kind) => {
          const rec = checks[kind];
          const summaryLine = summarise(kind, rec);
          return (
            <div key={kind} className="py-2.5 flex items-start gap-3" data-testid={`${testId}-row-${kind}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-heading text-ink flex items-center gap-2">
                  {LABELS[kind]}
                  {rec?.locked && <Lock size={12} className="text-emerald-700" />}
                </div>
                {summaryLine && (
                  <div className="text-xs text-ink-soft truncate mt-0.5">{summaryLine}</div>
                )}
                {rec?.admin_override && (
                  <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700 mt-0.5">
                    Admin {rec.admin_override.action} · {rec.admin_override.note || "no note"}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <StatusPill rec={rec} />
                {rec && rec.status === "succeeded" && rec.locked && (
                  <button
                    onClick={() => runOverride(rec, "unlock")}
                    disabled={overrideBusy === `${rec.verification_id}:unlock`}
                    className="text-[11px] font-heading text-[#8A6600] hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid={`${testId}-unlock-${kind}`}
                    title="Allow user to re-enter this field"
                  >
                    <Unlock size={11} /> Unlock
                  </button>
                )}
                {rec && rec.status === "succeeded" && (
                  <button
                    onClick={() => runOverride(rec, "reject")}
                    disabled={overrideBusy === `${rec.verification_id}:reject`}
                    className="text-[11px] font-heading text-rose-700 hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid={`${testId}-reject-${kind}`}
                    title="Mark this verification as rejected"
                  >
                    <XCircle size={11} /> Reject
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!summary && !busy && (
        <div className="text-xs text-ink-muted italic mt-2">No sandbox checks run yet for this user.</div>
      )}

      <ConfirmReasonDialog
        open={!!overrideFor}
        title={
          overrideFor?.action === "reject"
            ? `Reject ${LABELS[Object.keys(LABELS).find((k) => (summary?.checks || {})[k]?.verification_id === overrideFor?.rec?.verification_id) || ""] || "verification"}?`
            : `Unlock ${LABELS[Object.keys(LABELS).find((k) => (summary?.checks || {})[k]?.verification_id === overrideFor?.rec?.verification_id) || ""] || "verification"}?`
        }
        description={
          overrideFor?.action === "reject"
            ? "The user will lose the verified badge for this check and be required to re-submit."
            : "The user will be able to re-enter this field. Existing verification is preserved in the audit log."
        }
        confirmLabel={overrideFor?.action === "reject" ? "Reject verification" : "Unlock field"}
        tone={overrideFor?.action === "reject" ? "danger" : "gold"}
        reasonRequired={overrideFor?.action === "reject"}
        reasonLabel={overrideFor?.action === "reject" ? "Reason (required)" : "Unlock reason (optional)"}
        reasonPlaceholder=""
        onConfirm={confirmOverride}
        onClose={() => setOverrideFor(null)}
        testId={`${testId}-override-dialog`}
      />
      <ConfirmReasonDialog
        open={bulkConfirmOpen}
        title="Run all Sandbox company checks?"
        description="Runs CIN, GSTIN, Company PAN and DIN using data already on the startup profile. Missing inputs are skipped. This burns wallet credits on Sandbox."
        confirmLabel="Run All Checks"
        tone="gold"
        reasonRequired={false}
        reasonLabel="Optional note (audit log only)"
        reasonPlaceholder=""
        onConfirm={confirmBulk}
        onClose={() => setBulkConfirmOpen(false)}
        testId={`${testId}-bulk-dialog`}
      />
    </Card>
  );
}
